/**
 * 👥 UsuarioList.jsx
 * Lista de todos los usuarios registrados, con búsqueda, filtros y
 * gestión del estado de la cuenta (ACTIVO <-> INACTIVO).
 */
import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Users, UserX, UserCheck, Mail, Phone, Search, Filter, X, MailWarning } from "lucide-react";
import Swal from "sweetalert2";
import { getAllUsuarios, desactivarUsuario, reactivarUsuario } from "../../services/UsuarioService";
import {
    ESTADO_ACTIVO,
    ESTADO_BLOQUEADO,
    ESTADO_INACTIVO,
    ESTADO_PENDIENTE,
    TODOS,
    accionEstadoDisponible,
    contarPorEstado,
    contarPorRol,
    estadoDe,
    filtrarUsuarios,
    hayFiltrosActivos,
} from "./usuarioFilters";

const ROLES = ["ADMIN", "ANFITRION", "SOCIO", "USUARIO_BASICO"];

const ROL_BADGE = {
    ADMIN: "bg-red-100 text-red-700",
    ANFITRION: "bg-blue-100 text-blue-700",
    SOCIO: "bg-green-100 text-green-700",
    USUARIO_BASICO: "bg-purple-100 text-purple-700",
};

// Cada estado se lee distinto del resto: antes INACTIVO y PENDIENTE se pintaban igual
const ESTADO_UI = {
    [ESTADO_ACTIVO]: {
        etiqueta: "ACTIVO",
        clase: "bg-green-100 text-green-700",
        titulo: "La cuenta puede acceder al sistema",
    },
    [ESTADO_INACTIVO]: {
        etiqueta: "INACTIVO",
        clase: "bg-gray-100 text-gray-700",
        titulo: "Dado de baja por un administrador",
    },
    [ESTADO_PENDIENTE]: {
        etiqueta: "PENDIENTE",
        clase: "bg-amber-100 text-amber-700",
        titulo: "No ha verificado su correo",
    },
    [ESTADO_BLOQUEADO]: {
        etiqueta: "BLOQUEADO",
        clase: "bg-red-100 text-red-700",
        titulo: "Cuenta bloqueada",
    },
};

const getRolBadge = (rol) => ROL_BADGE[rol] || "bg-gray-100 text-gray-700";

const estadoUi = (usuario) =>
    ESTADO_UI[estadoDe(usuario)] || {
        etiqueta: estadoDe(usuario),
        clase: "bg-gray-100 text-gray-700",
        titulo: "",
    };

export default function UsuarioList() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroRol, setFiltroRol] = useState(TODOS);
    const [filtroEstado, setFiltroEstado] = useState(TODOS);
    const [busqueda, setBusqueda] = useState("");
    const [procesandoId, setProcesandoId] = useState(null);

    // Mantiene el tecleo fluido sin retrasar el resultado con un timer artificial
    const busquedaDiferida = useDeferredValue(busqueda);

    const fetchUsuarios = async () => {
        try {
            setLoading(true);
            const response = await getAllUsuarios();
            setUsuarios(response.data);
        } catch (error) {
            console.error("Error al cargar usuarios:", error);
            Swal.fire("Error", "No se pudieron cargar los usuarios", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsuarios();
    }, []);

    const usuariosFiltrados = useMemo(
        () => filtrarUsuarios(usuarios, { busqueda: busquedaDiferida, rol: filtroRol, estado: filtroEstado }),
        [usuarios, busquedaDiferida, filtroRol, filtroEstado]
    );

    const filtrosActivos = hayFiltrosActivos({
        busqueda: busquedaDiferida,
        rol: filtroRol,
        estado: filtroEstado,
    });

    const limpiarFiltros = () => {
        setBusqueda("");
        setFiltroRol(TODOS);
        setFiltroEstado(TODOS);
    };

    const mensajeDeError = (error, porDefecto) =>
        error?.response?.data?.message || porDefecto;

    // Baja administrativa: ACTIVO -> INACTIVO
    const handleDesactivar = async (usuario) => {
        const nombre = `${usuario.nombre} ${usuario.apellido}`.trim();
        const result = await Swal.fire({
            title: "¿Desactivar usuario?",
            text: `${nombre} no podrá acceder al sistema ni usar su QR en los clubes`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            confirmButtonText: "Sí, desactivar",
            cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        try {
            setProcesandoId(usuario.id);
            await desactivarUsuario(usuario.id);
            Swal.fire("Desactivado", "Usuario desactivado", "info");
            fetchUsuarios();
        } catch (error) {
            Swal.fire("Error", mensajeDeError(error, "No se pudo desactivar"), "error");
        } finally {
            setProcesandoId(null);
        }
    };

    // Alta administrativa: INACTIVO -> ACTIVO
    const handleReactivar = async (usuario) => {
        const nombre = `${usuario.nombre} ${usuario.apellido}`.trim();
        const result = await Swal.fire({
            title: "¿Reactivar usuario?",
            text: `${nombre} recuperará el acceso. Conserva su membresía y su QR actual.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            confirmButtonText: "Sí, reactivar",
            cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        try {
            setProcesandoId(usuario.id);
            await reactivarUsuario(usuario.id);
            Swal.fire("Reactivado", "El usuario ya puede acceder de nuevo", "success");
            fetchUsuarios();
        } catch (error) {
            Swal.fire("Error", mensajeDeError(error, "No se pudo reactivar"), "error");
        } finally {
            setProcesandoId(null);
        }
    };

    // Chips de estado: BLOQUEADO solo aparece si existe alguno
    const chipsEstado = [
        { valor: TODOS, etiqueta: "Todos", cantidad: usuarios.length },
        { valor: ESTADO_ACTIVO, etiqueta: "Activos", cantidad: contarPorEstado(usuarios, ESTADO_ACTIVO) },
        { valor: ESTADO_INACTIVO, etiqueta: "Inactivos", cantidad: contarPorEstado(usuarios, ESTADO_INACTIVO) },
        { valor: ESTADO_PENDIENTE, etiqueta: "Pendientes", cantidad: contarPorEstado(usuarios, ESTADO_PENDIENTE) },
        { valor: ESTADO_BLOQUEADO, etiqueta: "Bloqueados", cantidad: contarPorEstado(usuarios, ESTADO_BLOQUEADO) },
    ].filter((chip) => chip.valor !== ESTADO_BLOQUEADO || chip.cantidad > 0);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin h-8 w-8 border-4 border-herbalife-green border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header con búsqueda y filtros */}
            <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Usuarios</h2>
                            <p className="text-sm text-gray-500">
                                {filtrosActivos
                                    ? `${usuariosFiltrados.length} de ${usuarios.length} usuarios`
                                    : `${usuarios.length} usuarios registrados`}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Buscador */}
                        <div className="relative group w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-herbalife-green transition-colors" />
                            <input
                                type="search"
                                aria-label="Buscar usuario"
                                placeholder="Nombre, correo o teléfono..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                className="pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm w-full focus:outline-none focus:ring-2 focus:ring-herbalife-green/30 focus:border-herbalife-green focus:bg-white transition-all shadow-sm"
                            />
                        </div>

                        {/* Filtro por rol */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                aria-label="Filtrar por rol"
                                value={filtroRol}
                                onChange={(e) => setFiltroRol(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-herbalife-green outline-none"
                            >
                                <option value={TODOS}>Todos los roles ({usuarios.length})</option>
                                {ROLES.map((rol) => (
                                    <option key={rol} value={rol}>
                                        {rol} ({contarPorRol(usuarios, rol)})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Filtro por estado */}
                <div className="flex flex-wrap items-center gap-2 mt-6">
                    {chipsEstado.map((chip) => {
                        const activo = filtroEstado === chip.valor;
                        return (
                            <button
                                key={chip.valor}
                                type="button"
                                onClick={() => setFiltroEstado(chip.valor)}
                                aria-pressed={activo}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${activo
                                    ? "bg-herbalife-green text-white border-herbalife-green"
                                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                                    }`}
                            >
                                {chip.etiqueta} ({chip.cantidad})
                            </button>
                        );
                    })}

                    {filtrosActivos && (
                        <button
                            type="button"
                            onClick={limpiarFiltros}
                            className="ml-1 flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-full transition-colors"
                        >
                            <X className="w-3.5 h-3.5" />
                            Limpiar filtros
                        </button>
                    )}
                </div>
            </div>

            {/* Tabla de usuarios */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {usuariosFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-gray-100">
                                            <Search className="w-8 h-8 text-gray-300" />
                                        </div>
                                        <h3 className="font-semibold text-gray-700">Sin resultados</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {busquedaDiferida.trim()
                                                ? `Ningún usuario coincide con "${busquedaDiferida.trim()}"`
                                                : "No hay usuarios con los filtros seleccionados"}
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                usuariosFiltrados.map((usuario) => {
                                    const ui = estadoUi(usuario);
                                    const accion = accionEstadoDisponible(usuario);
                                    const procesando = procesandoId === usuario.id;

                                    return (
                                        <tr key={usuario.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium text-gray-800">
                                                        {usuario.nombre} {usuario.apellido}
                                                    </p>
                                                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                                        <Mail className="w-3 h-3" />
                                                        {usuario.email}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {usuario.telefono ? (
                                                    <div className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {usuario.telefono}
                                                    </div>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRolBadge(usuario.rolNombre)}`}>
                                                    {usuario.rolNombre || "Sin rol"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span
                                                    title={ui.titulo}
                                                    className={`px-3 py-1 rounded-full text-xs font-medium ${ui.clase}`}
                                                >
                                                    {ui.etiqueta}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-end gap-2">
                                                    {accion === "DESACTIVAR" && (
                                                        <button
                                                            onClick={() => handleDesactivar(usuario)}
                                                            disabled={procesando}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-40"
                                                            title="Desactivar"
                                                            aria-label={`Desactivar a ${usuario.nombre} ${usuario.apellido}`}
                                                        >
                                                            <UserX className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {accion === "REACTIVAR" && (
                                                        <button
                                                            onClick={() => handleReactivar(usuario)}
                                                            disabled={procesando}
                                                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-40"
                                                            title="Reactivar"
                                                            aria-label={`Reactivar a ${usuario.nombre} ${usuario.apellido}`}
                                                        >
                                                            <UserCheck className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {estadoDe(usuario) === ESTADO_PENDIENTE && (
                                                        <span
                                                            className="flex items-center gap-1 text-xs text-amber-600"
                                                            title="Se resuelve cuando el usuario verifica su correo"
                                                        >
                                                            <MailWarning className="w-4 h-4" />
                                                            Esperando verificación
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
